import { Button, makeStyles, Typography } from "@material-ui/core";
import ActionDialogVariant from "@saleor/components/ActionDialogVariant";
import Container from "@saleor/components/Container";
import CouponCollectionEdit from "@saleor/coupons/components/CouponCollectionEdit";
import {
  useCouponCollectionCreateDeepLink,
  useCouponCollectionUpdate,
  useRemoveCouponToCollection,
  useToggleCouponCollectionActivation
} from "@saleor/coupons/mutations";
import {
  useCouponCollectionAssignedCouponList,
  useCouponCollectionDetailQuery
} from "@saleor/coupons/queries";
import { CouponCollectionAssignedCouponsListVariables } from "@saleor/coupons/types/CouponCollectionAssignedCouponsList";
import { CouponCollectionUpdate } from "@saleor/coupons/types/CouponCollectionUpdate";
import { ToggleCouponCollectionActivation } from "@saleor/coupons/types/ToggleCouponCollectionActivation";
import {
  CouponCollectionDetailsQueryParams,
  couponCollectionViewPath,
  couponCollectionViewUrl
} from "@saleor/coupons/url";
import useBulkActions from "@saleor/hooks/useBulkActions";
import useListSettings from "@saleor/hooks/useListSettings";
import useNavigator from "@saleor/hooks/useNavigator";
import useNotifier from "@saleor/hooks/useNotifier";
import usePaginator, {
  createPaginationState
} from "@saleor/hooks/usePaginator";
import { maybe } from "@saleor/misc";
import { ListViews } from "@saleor/types";
import {
  CouponCollectionStatusEnum,
  CouponSortField,
  CouponStatusEnum,
  OrderDirection
} from "@saleor/types/globalTypes";
import createDialogActionHandlers from "@saleor/utils/handlers/dialogActionHandlers";
import { changeTimeZone } from "@saleor/utils/smop/changeTimeZoneDateTime";
import { CouponCollectionFormData } from "@saleor/utils/validators/couponCollection";
import Moment from "moment";
import React, { useEffect, useMemo, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
export interface CouponCollectionDetailsViewProps {
  id: string;
  params: CouponCollectionDetailsQueryParams;
}

const useStyles = makeStyles(
  () => ({
    subContent: {
      color: "#747C8C",
      fontSize: "14px",
      padding: "0 1em",
      textAlign: "center"
    }
  }),
  { name: "CouponCollectionDetailsPage" }
);

export const CouponCollectionDetailsPage: React.FC<CouponCollectionDetailsViewProps> = ({
  id,
  params
}) => {
  const navigate = useNavigator();
  const intl = useIntl();
  const notify = useNotifier();
  const paginate = usePaginator();
  const classes = useStyles({});
  const [couponCollectionImage, setCouponCollectionImageUpload] = useState(
    null
  );

  const [openModal, closeModal] = createDialogActionHandlers(
    navigate,
    params => couponCollectionViewUrl(id, params),
    params
  );

  const [couponsToRemove, setCouponsToRemove] = useState<Array<string | null>>(
    []
  );

  const { isSelected, listElements, toggle, toggleAll, reset } = useBulkActions(
    params.ids
  );
  const { settings, updateListSettings } = useListSettings(
    ListViews.COUPON_COLLECTION_ASSIGNED_COUPONS_LIST
  );
  const paginationState = createPaginationState(settings?.rowNumber, params);
  const queryVariables = useMemo<CouponCollectionAssignedCouponsListVariables>(
    () => ({
      ...paginationState,
      id,
      sortBy: {
        direction: OrderDirection.DESC,
        field: CouponSortField.ASSIGNED_AT
      }
    }),
    [params, settings?.rowNumber]
  );

  const {
    data: coupons,
    refetch: assignedCouponsRefetch
  } = useCouponCollectionAssignedCouponList({
    displayLoader: true,
    variables: queryVariables
  });
  const assignedCoupons = coupons?.couponCollection?.coupons?.edges?.map(
    edges => edges.node
  );

  const { loadNextPage, loadPreviousPage, pageInfo } = paginate(
    maybe(() => coupons?.couponCollection?.coupons?.pageInfo),
    paginationState,
    params
  );

  const { data, loading, refetch } = useCouponCollectionDetailQuery({
    displayLoader: true,
    variables: {
      id
    }
  });
  const couponCollection = data?.couponCollection;

  const handleUpdateSuccess = async (data: CouponCollectionUpdate) => {
    if (data?.couponCollectionUpdate?.errors?.length === 0) {
      if (data.couponCollectionUpdate.couponCollection.status === "DRAFT") {
        notify({
          status: "success",
          text: `${data.couponCollectionUpdate.couponCollection.title} updated successfully.`
        });
      } else {
        notify({
          status: "success",
          text: `${data.couponCollectionUpdate.couponCollection.title} published successfully.`
        });
      }
      refetch();
      setCouponCollectionImageUpload(null);
      await assignedCouponsRefetch();
    } else if (data?.couponCollectionUpdate?.errors) {
      notify({
        status: "error",
        text: data.couponCollectionUpdate?.errors[0].message
      });
    }
  };

  const [
    updateCouponCollection,
    updateCuponCollectionResult
  ] = useCouponCollectionUpdate({
    onCompleted: handleUpdateSuccess
  });

  const onDraft = (formData: CouponCollectionFormData) => {
    handleSubmit(formData, CouponStatusEnum.DRAFT);
  };

  const onPublish = (formData: CouponCollectionFormData) => {
    const dateNow = new Date().toLocaleString() + "";
    if (formData.startDate === Moment(dateNow).format("YYYY-MM-DD")) {
      handleSubmit(formData, CouponStatusEnum.ACTIVE);
    } else {
      handleSubmit(formData, CouponStatusEnum.SCHEDULED);
    }
  };

  const [
    createDeeplink,
    createDeeplinkOpts
  ] = useCouponCollectionCreateDeepLink({
    onCompleted: data => {
      const errors = data?.couponCollectionGenerateDynamicLink?.discountErrors;
      if (errors?.length === 0) {
        refetch();
        assignedCouponsRefetch();
      } else {
        notify({
          status: "error",
          text: intl.formatMessage({
            defaultMessage: "Oops, something went wrong...",
            description: "snackbar text"
          })
        });
      }
    }
  });

  const handleGenerateDeeplink = (id: string) => {
    if (
      couponCollection &&
      couponCollection.status !== CouponCollectionStatusEnum.DRAFT
    ) {
      createDeeplink({ variables: { id } });
    }
  };

  const handleSubmit = async (
    formData: CouponCollectionFormData,
    status: string
  ) => {
    const couponCollectionUpdateInput = {
      ...(couponCollectionImage && {
        bannerImage: formData.bannerImage
      }),
      categories: formData.categories,
      classification: formData.classification,
      collectionType: formData.collectionType,
      endDate: changeTimeZone(formData.endDate, formData.endTime),
      isFeatured: formData.isFeatured,
      isHidden: formData.isHidden,
      startDate: changeTimeZone(formData.startDate, formData.startTime),
      status,
      title: formData.title,
      writeup: JSON.stringify(formData.writeup),
      slug: formData.slug || null
    };

    await updateCouponCollection({
      variables: {
        id,
        input: couponCollectionUpdateInput
      }
    });
  };

  const handleToggleActivationSuccess = async (
    data: ToggleCouponCollectionActivation
  ) => {
    if (data?.couponCollectionToggleActivate?.errors?.length === 0) {
      const collectionStatus =
        data?.couponCollectionToggleActivate?.couponCollection?.status;
      const successMsg =
        collectionStatus === "INACTIVE"
          ? "Coupon Collection Successfully Deactivated"
          : "Coupon Collection Activated Successfully";
      notify({
        status: "success",
        text: successMsg
      });

      if (collectionStatus === "INACTIVE") {
        closeModal();
        await refetch();
        await assignedCouponsRefetch();
      } else {
        setTimeout(() => {
          window.location.reload();
        }, 1000);
      }
    } else {
      notify({
        status: "error",
        text: data.couponCollectionToggleActivate?.errors[0].message
      });
      closeModal();
    }
  };

  const [
    toggleCouponCollectionActivation,
    toggleCouponCollectionActivationResult
  ] = useToggleCouponCollectionActivation({
    onCompleted: handleToggleActivationSuccess
  });

  const handleCouponCollectionToggle = bool => {
    toggleCouponCollectionActivation({
      variables: {
        activate: bool,
        couponCollectionId: couponCollection?.id
      }
    });
  };

  const [
    removeCouponToCollection,
    removeCouponToCollectionResult
  ] = useRemoveCouponToCollection({
    onCompleted: data => {
      if (data.setCouponsToCollection.errors.length === 0) {
        setCouponsToRemove([]);
        reset();
        notify({
          status: "success",
          text: "Assigned coupon(s) has been removed successfully."
        });
      } else {
        notify({
          status: "error",
          text: data.setCouponsToCollection.errors[0].message
        });
      }
      assignedCouponsRefetch();
    }
  });

  const handleRemoveCouponToCollection = (couponId: string) => {
    setCouponsToRemove([couponId]);
    openModal("unassign");
  };

  const handleRemoveSelected = () => {
    setCouponsToRemove([...listElements]);
    openModal("unassign");
  };

  const confirmRemoveCouponToCollection = async () => {
    closeModal();
    await removeCouponToCollection({
      variables: {
        coupons: { remove: couponsToRemove },
        instanceId: couponCollection.id
      }
    });
  };

  useEffect(() => {
    const url = window.location.href;
    if (!url.includes("coupon-ranking")) {
      createDeeplink({ variables: { id } });
    }
  }, []);

  useEffect(() => {
    navigate(couponCollectionViewPath(id));
  }, [settings?.rowNumber]);

  return (
    <Container>
      <CouponCollectionEdit
        assignedCoupons={assignedCoupons}
        listElements={listElements}
        toggle={toggle}
        toggleAll={toggleAll}
        isChecked={isSelected}
        collection={couponCollection}
        onPublish={onPublish}
        onDraft={onDraft}
        onBack={() => navigate("/coupons/collection")}
        onCouponCollectionImageUpload={setCouponCollectionImageUpload}
        onGenerateDeeplink={handleGenerateDeeplink}
        couponCollectionImage={couponCollectionImage}
        saveButtonBarState={updateCuponCollectionResult.status}
        disabled={
          loading ||
          createDeeplinkOpts.loading ||
          updateCuponCollectionResult.loading
        }
        settings={settings}
        onUpdateListSettings={updateListSettings}
        toolbar={
          <Button color="primary" onClick={handleRemoveSelected}>
            {intl.formatMessage({
              defaultMessage: "Remove Selected",
              description: "Remove selected coupons"
            })}
          </Button>
        }
        selected={listElements.length}
        onNextPage={loadNextPage}
        onPreviousPage={loadPreviousPage}
        pageInfo={pageInfo}
        onRowClick={undefined}
        onDeactivate={() => openModal("deactivate")}
        onActivate={() => handleCouponCollectionToggle(true)}
        onUnassign={couponId => handleRemoveCouponToCollection(couponId)}
      />
      <ActionDialogVariant
        confirmButtonState={toggleCouponCollectionActivationResult.status}
        onClose={closeModal}
        onConfirm={() => handleCouponCollectionToggle(false)}
        warningTitle={intl.formatMessage({
          defaultMessage: "Deactivate Coupon Collection",
          description: "dialog title"
        })}
        open={params.action === "deactivate"}
        confirmButtonLabel="Yes"
        buttonsAlignment="center"
        maxWidth="sm"
      >
        <FormattedMessage defaultMessage="Are you sure you want to deactivate the selected coupon collection(s)?" />
        <Typography className={classes.subContent}>
          <FormattedMessage defaultMessage="Coupon Collection(s) selected will be deactivated." />
        </Typography>
      </ActionDialogVariant>
      <ActionDialogVariant
        confirmButtonState={removeCouponToCollectionResult.status}
        onClose={closeModal}
        onConfirm={confirmRemoveCouponToCollection}
        warningTitle={intl.formatMessage({
          defaultMessage: "Remove Assigned Coupon(s)?",
          description: "dialog title"
        })}
        open={params.action === "unassign"}
        confirmButtonLabel="Proceed"
        closeButtonLabel="retain"
        buttonsAlignment="center"
        maxWidth="sm"
      >
        <Typography className={classes.subContent}>
          <FormattedMessage defaultMessage="Once you proceed, the coupon(s) will be removed from this coupon collection" />
        </Typography>
      </ActionDialogVariant>
    </Container>
  );
};

export default CouponCollectionDetailsPage;
